import { CODE_GAMEPAD_URL } from '~/config/endpoints.config';

/** Page manette (téléphone joueur) : /gamepad/{sessionID} (casse ignorée, comme le router). */
export function isGamepadPath(pathname: string) {
    const path = pathname.toLowerCase();
    return path === `/${CODE_GAMEPAD_URL}` || path.startsWith(`/${CODE_GAMEPAD_URL}/`);
}

/**
 * iOS < 18.4 : l'API Wake Lock existe (depuis 16.4) mais peut répondre « OK »
 * sans effet (apps installées sur l'écran d'accueil, navigateurs intégrés aux
 * apps) — aucun repli ne se déclencherait. On y garde la vidéo.
 * iPadOS 13+ se présente comme un Mac (« Macintosh ») : on le reconnaît à
 * l'écran tactile et on lit la version de Safari, alignée sur celle d'iPadOS.
 */
export function isOldIOS(userAgent: string, maxTouchPoints = 0) {
    let m = userAgent.match(/(?:iPhone|iPad|iPod)[^)]* OS (\d+)_(\d+)/);
    if (!m && /Macintosh/.test(userAgent) && maxTouchPoints > 1) {
        m = userAgent.match(/Version\/(\d+)\.(\d+)/);
        // iPad sans version lisible : prudence, vidéo.
        if (!m) return true;
    }
    if (!m) return false;
    const major = Number(m[1]);
    const minor = Number(m[2]);
    return major < 18 || (major === 18 && minor < 4);
}

/**
 * Faut-il la vidéo anti-veille d'emblée ? Oui partout sauf sur la manette d'un
 * navigateur fiable pour l'API Wake Lock (Chrome Android 84+, Safari iOS 18.4+).
 * La vidéo pèse 6,8 Mo : chargée par chaque téléphone qui scanne le QR, c'était
 * le premier poste de poids de la manette. L'écran TV garde la vidéo (un seul
 * appareil, et certaines TV ignorent le wake lock).
 */
export function needsVideoUpFront(pathname: string, hasWakeLock: boolean, userAgent = '', maxTouchPoints = 0) {
    return !(isGamepadPath(pathname) && hasWakeLock && !isOldIOS(userAgent, maxTouchPoints));
}

type WakeLockLike = { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
type WakeLockSentinelLike = {
    readonly released?: boolean;
    release: () => Promise<void>;
    addEventListener?: (type: 'release', listener: () => void) => void;
};
type DocumentLike = Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'>;

/** Nouvel essai après un refus de reprise, page visible (le refus est souvent passager). */
export const RETRY_DELAYS_MS = [1000, 3000];
/** Une demande sans réponse au-delà = refus (sinon : ni verrou, ni vidéo, pour de bon). */
export const REQUEST_TIMEOUT_MS = 5000;

/**
 * Tient le verrou d'écran tant que la page est visible : le reprend au retour
 * au premier plan ET quand le navigateur le relâche de lui-même (économie
 * d'énergie…).
 * Repli vidéo (`onFail`, définitif) seulement si :
 *  - la TOUTE PREMIÈRE demande est refusée (navigateur qui ne l'autorise pas) ;
 *  - une reprise est refusée page visible, puis encore après RETRY_DELAYS_MS ;
 *  - le verrou est relâché en boucle aussitôt accordé.
 * Une demande sans réponse après REQUEST_TIMEOUT_MS compte comme un refus.
 * Une seule demande à la fois. Renvoie la fonction de nettoyage.
 */
export function holdScreenWakeLock(
    wakeLock: WakeLockLike,
    doc: DocumentLike,
    onFail: () => void,
    schedule: (fn: () => void, ms: number) => unknown = setTimeout
) {
    let sentinel: WakeLockSentinelLike | null = null;
    let disposed = false;
    let failed = false;
    let pending = false;
    let everGranted = false;
    let retries = 0;
    // Verrou relâché aussitôt accordé, en boucle = navigateur qui ne le tient pas : vidéo.
    let quickReleases = 0;

    const fail = () => {
        failed = true;
        if (!disposed) onFail();
    };

    // Demande bornée dans le temps ; un verrou accordé APRÈS le délai est relâché aussitôt.
    const requestWithTimeout = () =>
        new Promise<WakeLockSentinelLike>((resolve, reject) => {
            let settled = false;
            schedule(() => {
                if (settled) return;
                settled = true;
                reject(new Error('wake lock request timeout'));
            }, REQUEST_TIMEOUT_MS);
            wakeLock.request('screen').then(
                (lock) => {
                    if (settled) {
                        lock.release().catch(() => {});
                        return;
                    }
                    settled = true;
                    resolve(lock);
                },
                (error) => {
                    if (settled) return;
                    settled = true;
                    reject(error);
                }
            );
        });

    const request = async () => {
        // Verrou relâché sans que son événement nous parvienne : ne pas s'y fier.
        if (sentinel?.released) sentinel = null;
        if (disposed || failed || pending || sentinel || doc.visibilityState !== 'visible') return;
        pending = true;
        try {
            const next = await requestWithTimeout();
            if (disposed) {
                // Démonté pendant la demande : ne pas garder un verrou orphelin.
                next.release().catch(() => {});
                return;
            }
            sentinel = next;
            everGranted = true;
            retries = 0;
            const grantedAt = Date.now();
            next.addEventListener?.('release', () => {
                if (sentinel !== next) return; // relâché par notre nettoyage
                sentinel = null;
                if (disposed) return;
                // Seuls comptent les relâchements page visible (passer d'une app à l'autre est normal).
                const quick = doc.visibilityState === 'visible' && Date.now() - grantedAt < 2000;
                quickReleases = quick ? quickReleases + 1 : 0;
                if (quickReleases >= 3) fail();
                else request();
            });
        } catch {
            if (!everGranted) {
                fail();
            } else if (doc.visibilityState === 'visible') {
                // Reprise refusée page visible : réessayer, puis vidéo si ça persiste.
                if (retries >= RETRY_DELAYS_MS.length) fail();
                else schedule(request, RETRY_DELAYS_MS[retries++]);
            }
            // Page masquée : on retentera au prochain retour au premier plan.
        } finally {
            pending = false;
        }
    };
    const onVisibilityChange = () => {
        // Nouveau passage au premier plan : nouvelle série d'essais.
        if (doc.visibilityState === 'visible') retries = 0;
        request();
    };

    request();
    doc.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
        disposed = true;
        doc.removeEventListener('visibilitychange', onVisibilityChange);
        const held = sentinel;
        sentinel = null;
        held?.release().catch(() => {});
    };
}
